import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { STRIPE_PUBLIC_KEY } from '../config';
import { Button } from './ui/button';
import { Label } from './ui/label';

// Inicializar Stripe (se inicializa una sola vez)
let stripePromiseInstance = null;
const getStripePromise = () => {
  if (!stripePromiseInstance) {
    stripePromiseInstance = loadStripe(STRIPE_PUBLIC_KEY);
  }
  return stripePromiseInstance;
};

// Componente del formulario de pago
const PaymentFormContent = ({ onSubmit, loading, error, amount, currency }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      const cardElement = elements.getElement(CardElement);
      
      if (!cardElement) {
        throw new Error('No se pudo obtener el elemento de tarjeta');
      }

      // Crear PaymentMethod directamente (esto lo guarda en Stripe)
      // IMPORTANTE: Asegurarse de que las claves de Stripe coincidan
      const publicKeyPrefix = STRIPE_PUBLIC_KEY ? STRIPE_PUBLIC_KEY.substring(0, 12) : 'NO_CONFIGURADO';
      const isTestMode = STRIPE_PUBLIC_KEY?.startsWith('pk_test_');
      
      console.log('[Stripe][FRONTEND][DIAGNOSTIC] ==========================================');
      console.log('[Stripe][FRONTEND][DIAGNOSTIC] Creando PaymentMethod', {
        'public_key_prefix': publicKeyPrefix,
        'public_key_mode': isTestMode ? 'TEST (pk_test_)' : 'LIVE (pk_live_)',
        'public_key_length': STRIPE_PUBLIC_KEY?.length || 0,
        'public_key_source': process.env.REACT_APP_STRIPE_PUBLIC_KEY ? 'ENV_VAR' : 'DEFAULT_HARDCODED',
      });
      
      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (stripeError) {
        console.error('❌ Error de Stripe al crear PaymentMethod:', stripeError);
        throw new Error(stripeError.message || 'Error al procesar la tarjeta');
      }

      if (!paymentMethod || !paymentMethod.id) {
        throw new Error('No se pudo crear el método de pago. Intenta nuevamente.');
      }

      // Verificar que el PaymentMethod tiene un ID válido
      if (!paymentMethod.id.startsWith('pm_')) {
        throw new Error('El método de pago no es válido. Intenta nuevamente.');
      }

      console.log('[Stripe][FRONTEND][DIAGNOSTIC] PaymentMethod creado exitosamente', {
        'payment_method_id': paymentMethod.id,
        'payment_method_livemode': paymentMethod.livemode,
        'payment_method_mode': paymentMethod.livemode ? 'LIVE' : 'TEST',
        'type': paymentMethod.type,
        'card_brand': paymentMethod.card?.brand,
        'card_last4': paymentMethod.card?.last4,
      });
      console.log('[Stripe][FRONTEND][DIAGNOSTIC] ==========================================');
      
      // Esperar un momento para asegurar que Stripe procese el PaymentMethod
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Llamar al callback con el paymentMethod
      await onSubmit(paymentMethod);
    } catch (err) {
      console.error('❌ Error en pago:', err);
      throw err;
    } finally {
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#ffffff',
        '::placeholder': {
          color: '#a2a2a2',
        },
        fontFamily: 'system-ui, -apple-system, sans-serif',
      },
      invalid: {
        color: '#ef4444',
        iconColor: '#ef4444',
      },
    },
    hidePostalCode: true,
    disableLink: true, // Desactivar Stripe Link
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-2">
        <Label className="text-white">Información de tarjeta</Label>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-2xl p-4">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
        <p className="text-[#A2A2A2] text-sm text-center">
          🔒 Pago procesado de forma segura por Stripe
        </p>
      </div>

      <Button
        type="submit"
        disabled={!stripe || processing || loading}
        className="w-full bg-[#24b183] text-white hover:bg-[#1f9a72] rounded-full py-6 text-lg font-semibold transition-all hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
      >
        {processing || loading ? 'Procesando...' : `Accede a CCE Puebla`}
      </Button>
    </form>
  );
};

// Componente wrapper con Elements
const PaymentForm = ({ onSubmit, loading, error, amount, currency }) => {
  return (
    <Elements 
      stripe={getStripePromise()}
      options={{
        disableLink: true, // Desactivar Stripe Link a nivel de Elements
        wallets: {
          link: false
        }
      }}
    >
      <PaymentFormContent
        onSubmit={onSubmit}
        loading={loading}
        error={error}
        amount={amount}
        currency={currency}
      />
    </Elements>
  );
};

export default PaymentForm;

